import { sovereignChecksum, stableJson } from './checksum';
import type { PersistedEnvelope, RecoveryReport, SovereignProject } from './domain';
import { migrateProject } from './migrations';

export interface SovereignStorageAdapter {
  read(key: string): Promise<unknown>;
  write(key: string, value: unknown): Promise<void>;
  remove(key: string): Promise<void>;
  clear(): Promise<void>;
}

export class MemorySovereignStorage implements SovereignStorageAdapter {
  readonly values = new Map<string, unknown>();
  read(key: string): Promise<unknown> {
    return Promise.resolve(this.values.get(key) ?? null);
  }
  write(key: string, value: unknown): Promise<void> {
    this.values.set(key, structuredClone(value));
    return Promise.resolve();
  }
  remove(key: string): Promise<void> {
    this.values.delete(key);
    return Promise.resolve();
  }
  clear(): Promise<void> {
    this.values.clear();
    return Promise.resolve();
  }
}

export class IndexedDbSovereignStorage implements SovereignStorageAdapter {
  readonly #databaseName: string;
  constructor(databaseName = 'noemir-sovereign-v1') {
    this.#databaseName = databaseName;
  }
  async #database(): Promise<IDBDatabase> {
    if (typeof indexedDB === 'undefined') throw new Error('IndexedDB is unavailable.');
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.#databaseName, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains('state'))
          request.result.createObjectStore('state');
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed.'));
    });
  }
  async #operation<T>(
    mode: IDBTransactionMode,
    run: (store: IDBObjectStore) => IDBRequest<T>,
  ): Promise<T> {
    const database = await this.#database();
    return new Promise((resolve, reject) => {
      const transaction = database.transaction('state', mode);
      const request = run(transaction.objectStore('state'));
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error('IndexedDB operation failed.'));
      transaction.oncomplete = () => database.close();
    });
  }
  read(key: string): Promise<unknown> {
    return this.#operation<unknown>(
      'readonly',
      (store) => store.get(key) as IDBRequest<unknown>,
    ).then((value) => value ?? null);
  }
  write(key: string, value: unknown): Promise<void> {
    return this.#operation('readwrite', (store) => store.put(value, key)).then(() => undefined);
  }
  remove(key: string): Promise<void> {
    return this.#operation('readwrite', (store) => store.delete(key)).then(() => undefined);
  }
  clear(): Promise<void> {
    return this.#operation('readwrite', (store) => store.clear()).then(() => undefined);
  }
}

const validEnvelope = (value: unknown): PersistedEnvelope | null => {
  if (value === null || typeof value !== 'object' || Array.isArray(value)) return null;
  const record = value as Readonly<Record<string, unknown>>;
  if (record.format !== 'noemir-project' || !Number.isInteger(record.generation)) return null;
  const envelope = record as unknown as PersistedEnvelope;
  return sovereignChecksum(envelope.payload) === envelope.checksum ? envelope : null;
};

export class SovereignProjectRepository {
  readonly #storage: SovereignStorageAdapter;
  readonly #primary = 'project:primary';
  readonly #recovery = 'project:recovery';
  #generation = 0;
  constructor(storage?: SovereignStorageAdapter) {
    this.#storage =
      storage ??
      (typeof indexedDB === 'undefined'
        ? new MemorySovereignStorage()
        : new IndexedDbSovereignStorage());
  }
  async #readOpfs(): Promise<unknown> {
    if (
      typeof navigator === 'undefined' ||
      !('storage' in navigator) ||
      !('getDirectory' in navigator.storage)
    )
      return null;
    try {
      const root = await navigator.storage.getDirectory();
      const directory = await root.getDirectoryHandle('noemir', { create: false });
      const handle = await directory.getFileHandle('project-recovery.json', { create: false });
      return JSON.parse(await (await handle.getFile()).text()) as unknown;
    } catch {
      return null;
    }
  }
  async #writeOpfs(envelope: PersistedEnvelope): Promise<void> {
    if (
      typeof navigator === 'undefined' ||
      !('storage' in navigator) ||
      !('getDirectory' in navigator.storage)
    )
      return;
    const root = await navigator.storage.getDirectory();
    const directory = await root.getDirectoryHandle('noemir', { create: true });
    const handle = await directory.getFileHandle('project-recovery.json', { create: true });
    const writable = await handle.createWritable();
    await writable.write(stableJson(envelope));
    await writable.close();
  }
  async load(): Promise<RecoveryReport> {
    const issues: string[] = [];
    for (const [source, key] of [
      ['primary', this.#primary],
      ['recovery', this.#recovery],
    ] as const) {
      const raw = await this.#storage.read(key);
      if (raw === null) continue;
      const envelope = validEnvelope(raw);
      if (envelope === null) {
        issues.push(`${source} snapshot checksum failed`);
        continue;
      }
      try {
        const project = migrateProject(envelope.payload);
        this.#generation = Math.max(this.#generation, envelope.generation);
        return { project, recovered: source === 'recovery', source, issues };
      } catch (error) {
        issues.push(error instanceof Error ? error.message : `${source} snapshot is unreadable`);
      }
    }
    const opfs = validEnvelope(await this.#readOpfs());
    if (opfs !== null) {
      try {
        const project = migrateProject(opfs.payload);
        this.#generation = Math.max(this.#generation, opfs.generation);
        return { project, recovered: true, source: 'opfs', issues };
      } catch (error) {
        issues.push(error instanceof Error ? error.message : 'OPFS snapshot is unreadable');
      }
    }
    return { project: null, recovered: false, source: 'empty', issues };
  }
  async save(project: SovereignProject): Promise<void> {
    const current = await this.#storage.read(this.#primary);
    if (validEnvelope(current) !== null) await this.#storage.write(this.#recovery, current);
    this.#generation += 1;
    const envelope: PersistedEnvelope = {
      format: 'noemir-project',
      schemaVersion: project.schemaVersion,
      generation: this.#generation,
      checksum: sovereignChecksum(project),
      payload: project,
    };
    await this.#storage.write(this.#primary, envelope);
    await this.#writeOpfs(envelope).catch(() => undefined);
  }
  async deleteAllData(): Promise<void> {
    await this.#storage.clear();
    this.#generation = 0;
    if (
      typeof navigator !== 'undefined' &&
      'storage' in navigator &&
      'getDirectory' in navigator.storage
    ) {
      const root = await navigator.storage.getDirectory();
      for await (const name of root.keys()) await root.removeEntry(name, { recursive: true });
    }
  }
}
